import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  ALGORITHM_VERSION,
  DEFAULT_SIGMA_FRACTION,
  EXPLORATORY_WARNING,
  type AnalysisResultDto,
  type DataSource,
  type RoiDto,
  type RoiGeometry,
} from "@gazelab/shared";
import { api, errorMessage } from "../../services/api.js";
import { useAsync } from "../../lib/useAsync.js";
import { loadImage, type OverlaySpec } from "../../lib/heatmapCanvas.js";
import { IDENTITY_VIEW, type ViewState } from "../../lib/zoom.js";
import { safeFilename } from "../../lib/format.js";
import { Alert, ErrorAlert, Spinner } from "../../components/ui/index.js";
import { Icon } from "../../components/ui/Icon.js";
import { NoticeStrip, Segmented, Tabs } from "../../components/ui/Layout.js";
import { HeatmapPanel } from "../../components/analysis/HeatmapPanel.js";
import { DivergingLegend, SequentialLegend } from "../../components/analysis/Legends.js";
import { ExclusionCompare, GroupCompare } from "../../components/analysis/GroupCompare.js";
import { RoiTable } from "../../components/analysis/RoiTable.js";
import { composePng, downloadBlob, type ExportPanel } from "../../components/analysis/exportPng.js";
import { useStudy } from "./StudyLayout.js";
import { VideoResultsPage } from "./VideoResultsPage.js";
import { ResultsContext, useResultsShared, type ResultsShared } from "./resultsShared.js";
import { StimulusPicker } from "../../components/analysis/StimulusPicker.js";
import { DEMO_EXPORT_NOTE, useDemo } from "./AdminLayout.js";

type ViewMode = "side" | "diff";
type BottomTab = "metrics" | "rois" | "exclusions" | "warnings" | "method";

/** Fuente predeterminada: reales si existe alguna sesión real completada; si no, demo sintética. */
function defaultSource(counts: Record<DataSource, { completed: number }>): DataSource {
  if (counts.webgazer.completed > 0) return "webgazer";
  if (counts.synthetic.completed > 0) return "synthetic";
  return "webgazer";
}

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Alto de la ventana (para que los mapas quepan sin desplazarse). */
function useViewportHeight() {
  const [h, setH] = useState(() => window.innerHeight);
  useEffect(() => {
    const on = () => setH(window.innerHeight);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return h;
}

/** Bloque del panel de controles. */
function ControlCard({ title, children, aside, tour }: { title: string; children: ReactNode; aside?: ReactNode; tour?: string }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-3.5 shadow-sm" aria-label={title} data-tour={tour}>
      <div className="mb-2 flex items-center justify-between gap-2">
        <h2 className="eyebrow">{title}</h2>
        {aside}
      </div>
      <div className="space-y-2.5">{children}</div>
    </section>
  );
}

/**
 * Resultados: la vista depende del estímulo elegido (comparación sobre una imagen o análisis
 * temporal de un video). Estímulo, grupos y fuente se comparten entre ambas vistas.
 */
export function StudyResultsPage() {
  const { study } = useStudy();
  const stimuli = useAsync(() => api.listImages(study.id), [study.id]);
  const list = useMemo(() => (stimuli.data ?? []).filter((s) => s.processingStatus === "ready"), [stimuli.data]);
  const [stimulusId, setStimulusId] = useState("");
  const [groupA, setGroupA] = useState<string>(study.groups[0]?.id ?? "");
  const [groupB, setGroupB] = useState<string>(study.groups[1]?.id ?? "");
  const [source, setSource] = useState<DataSource>(() => defaultSource(study.sessionCounts));
  const [includeWarn, setIncludeWarn] = useState(true);
  useEffect(() => {
    if (!list.some((s) => s.id === stimulusId) && list.length) setStimulusId(list[0]!.id);
  }, [list, stimulusId]);
  const current = list.find((s) => s.id === stimulusId) ?? null;
  const shared: ResultsShared = {
    stimuli: list,
    loading: stimuli.loading,
    error: stimuli.error,
    stimulusId: current?.id ?? "",
    setStimulusId,
    current,
    groupA,
    setGroupA,
    groupB,
    setGroupB,
    source,
    setSource,
    includeWarn,
    setIncludeWarn,
  };
  return <ResultsContext.Provider value={shared}>{current?.kind === "video" ? <VideoResultsPage /> : <ImageResultsPage />}</ResultsContext.Provider>;
}

function ImageResultsPage() {
  const { study } = useStudy();
  const exportsOff = useDemo()?.enabled === true;
  const shared = useResultsShared();
  const { stimulusId: imageId, groupA, setGroupA, groupB, setGroupB, source, setSource, includeWarn, setIncludeWarn } = shared;
  const groups = study.groups;
  const [sigma, setSigma] = useState(DEFAULT_SIGMA_FRACTION);
  const [opacity, setOpacity] = useState(70);
  const [diffThreshold, setDiffThreshold] = useState(10);
  const [mode, setMode] = useState<ViewMode>("side");
  const [tab, setTab] = useState<BottomTab>("metrics");
  const [view, setView] = useState<ViewState>(IDENTITY_VIEW);
  const [drawMode, setDrawMode] = useState(false);
  const [roiLabel, setRoiLabel] = useState("");
  const [hoverRoi, setHoverRoi] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [img, setImg] = useState<HTMLImageElement | null>(null);
  const debouncedSigma = useDebounced(sigma, 350);
  const vh = useViewportHeight();

  const image = shared.current;

  useEffect(() => {
    setImg(null);
    setView(IDENTITY_VIEW);
    if (!imageId) return;
    let cancelled = false;
    loadImage(api.adminImageUrl(study.id, imageId))
      .then((i) => !cancelled && setImg(i))
      .catch((e) => !cancelled && setError(errorMessage(e)));
    return () => {
      cancelled = true;
    };
  }, [study.id, imageId]);

  const rois = useAsync(() => (imageId ? api.listRois(study.id, imageId) : Promise.resolve([] as RoiDto[])), [study.id, imageId]);
  const canAnalyze = Boolean(imageId && groupA && groupB && groupA !== groupB);
  const analysis = useAsync<AnalysisResultDto | null>(
    () =>
      canAnalyze
        ? api.analysis(study.id, { imageId, groupA, groupB, source, sigma: debouncedSigma, includeWarnQuality: includeWarn })
        : Promise.resolve(null),
    [study.id, imageId, groupA, groupB, source, debouncedSigma, includeWarn, rois.data],
  );
  const a = analysis.data ?? null;

  const overlayA: OverlaySpec | null = useMemo(
    () => (a?.groupA.heatmap ? { grid: a.groupA.heatmap, mode: "sequential", max: a.sharedMax, opacity: opacity / 100, threshold: 0.02 } : null),
    [a, opacity],
  );
  const overlayB: OverlaySpec | null = useMemo(
    () => (a?.groupB.heatmap ? { grid: a.groupB.heatmap, mode: "sequential", max: a.sharedMax, opacity: opacity / 100, threshold: 0.02 } : null),
    [a, opacity],
  );
  const overlayDiff: OverlaySpec | null = useMemo(
    () => (a?.difference ? { grid: a.difference, mode: "diverging", max: a.difference.max, opacity: opacity / 100, threshold: diffThreshold / 100 } : null),
    [a, opacity, diffThreshold],
  );

  async function addRoi(g: RoiGeometry) {
    setError(null);
    try {
      const label = roiLabel.trim() || `ROI ${(rois.data?.length ?? 0) + 1}`;
      await api.createRoi(study.id, { studyImageId: imageId, label, shape: "rect", geometry: g });
      setRoiLabel("");
      setTab("rois");
      await rois.reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  async function roiAction(fn: () => Promise<unknown>) {
    setError(null);
    try {
      await fn();
      await rois.reload();
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  async function exportPng(kind: "A" | "B" | "diff" | "comparison") {
    if (!a || !img || !image) return;
    setError(null);
    try {
      const titleA = `A: ${a.groupA.groupName} (n=${a.groupA.n})`;
      const titleB = `B: ${a.groupB.groupName} (n=${a.groupB.n})`;
      const panels: ExportPanel[] =
        kind === "A"
          ? [{ title: titleA, overlay: overlayA }]
          : kind === "B"
            ? [{ title: titleB, overlay: overlayB }]
            : kind === "diff"
              ? [{ title: `Diferencia A − B`, overlay: overlayDiff }]
              : [
                  { title: titleA, overlay: overlayA },
                  { title: titleB, overlay: overlayB },
                ];
      const blob = await composePng(img, panels, {
        studyTitle: study.title,
        imageName: image.filenameOriginal,
        analysis: a,
        legend: kind === "diff" ? "diverging" : "sequential",
        threshold: diffThreshold / 100,
      });
      const groupsPart =
        kind === "A" ? a.groupA.groupCode : kind === "B" ? a.groupB.groupCode : `${a.groupA.groupCode}-vs-${a.groupB.groupCode}`;
      const kindPart = kind === "diff" ? "diferencia" : kind === "comparison" ? "comparativa" : "heatmap";
      downloadBlob(
        blob,
        `${safeFilename(study.code, image.filenameOriginal.replace(/\.[^.]+$/, ""), kindPart, groupsPart, a.params.source === "synthetic" ? "SINTETICO" : "webgazer", `sigma${a.params.sigma}`)}.png`,
      );
    } catch (e) {
      setError(errorMessage(e));
    }
  }

  // Los mapas se dimensionan para caber en la ventana junto a las métricas.
  const panelMaxH = Math.max(260, vh - 470);
  const panelCommon = {
    image: img,
    naturalWidth: image?.naturalWidth ?? 4,
    naturalHeight: image?.naturalHeight ?? 3,
    view,
    onViewChange: setView,
    rois: rois.data ?? [],
    highlightRoiId: hoverRoi,
    drawMode,
    onRoiDrawn: addRoi,
  };
  const groupName = (id: string) => groups.find((g) => g.id === id)?.name ?? "";

  if (groups.length < 2) return <Alert tone="warning">El estudio necesita al menos dos grupos de edad para comparar.</Alert>;


  return (
    <div className="grid gap-4 xl:grid-cols-[17.5rem_minmax(0,1fr)]">
      {/* ---------------- Panel de controles (fijo al desplazarse) ---------------- */}
      <aside className="space-y-3 xl:sticky xl:top-5 xl:max-h-[calc(100vh-2.5rem)] xl:self-start xl:overflow-y-auto xl:pb-1" aria-label="Controles del análisis">
        <ControlCard tour="res-data" title="Datos">
          <label htmlFor="res-source" className="sr-only">
            Fuente de datos
          </label>
          <select id="res-source" className="input" value={source} onChange={(e) => setSource(e.target.value as DataSource)} data-testid="source-select">
            <option value="webgazer">Reales (webcam)</option>
            <option value="synthetic">SINTÉTICA (demo)</option>
          </select>
          <label htmlFor="res-warn" className="flex items-center gap-2 text-sm text-slate-700">
            <input id="res-warn" type="checkbox" checked={includeWarn} onChange={(e) => setIncludeWarn(e.target.checked)} />
            Incluir validaciones con advertencia
          </label>
        </ControlCard>

        <ControlCard tour="res-stimulus" title="Estímulo" aside={shared.stimuli.length > 0 && <span className="font-util text-xs text-slate-500">{shared.stimuli.findIndex((i) => i.id === imageId) + 1} / {shared.stimuli.length}</span>}>
          {shared.stimuli.length === 0 && !shared.loading && <p className="text-sm text-slate-500">Sube imágenes o videos al estudio para ver resultados.</p>}
          <StimulusPicker studyId={study.id} stimuli={shared.stimuli} value={imageId} onChange={shared.setStimulusId} />
        </ControlCard>

        <ControlCard
          tour="res-compare"
          title="Comparar"
          aside={
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold text-sky-800 hover:bg-sky-50"
              onClick={() => {
                setGroupA(groupB);
                setGroupB(groupA);
              }}
              title="Intercambiar A y B"
            >
              <Icon name="swap" className="h-3.5 w-3.5" /> Intercambiar
            </button>
          }
        >
          {(
            [
              ["res-a", "A", groupA, setGroupA, "bg-sky-700"],
              ["res-b", "B", groupB, setGroupB, "bg-amber-600"],
            ] as const
          ).map(([id, label, value, set, color]) => (
            <div key={id} className="flex items-center gap-2">
              <label htmlFor={id} className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold text-white ${color}`}>
                <span className="sr-only">Grupo </span>
                {label}
              </label>
              <select id={id} className="input py-1.5" value={value} onChange={(e) => set(e.target.value)}>
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
          {groupA === groupB && <p className="text-xs text-amber-700">Elige dos grupos distintos.</p>}
        </ControlCard>

        <ControlCard tour="res-map" title="Mapa">
          <Segmented
            label="Modo de vista"
            value={mode}
            onChange={setMode}
            options={[
              { value: "side", label: "A | B" },
              { value: "diff", label: "Diferencia" },
            ]}
          />
          <div>
            <label htmlFor="res-sigma" className="flex justify-between text-xs text-slate-600">
              <span>Suavizado σ</span>
              <span className="font-util">{(sigma * 100).toFixed(1)} %</span>
            </label>
            <input id="res-sigma" type="range" min={0.005} max={0.1} step={0.005} value={sigma} onChange={(e) => setSigma(Number(e.target.value))} className="w-full" />
          </div>
          <div>
            <label htmlFor="res-opacity" className="flex justify-between text-xs text-slate-600">
              <span>Opacidad</span>
              <span className="font-util">{opacity} %</span>
            </label>
            <input id="res-opacity" type="range" min={0} max={100} step={5} value={opacity} onChange={(e) => setOpacity(Number(e.target.value))} className="w-full" />
          </div>
          {mode === "diff" && (
            <div>
              <label htmlFor="res-thr" className="flex justify-between text-xs text-slate-600">
                <span>Umbral visible de diferencia</span>
                <span className="font-util">{diffThreshold} %</span>
              </label>
              <input id="res-thr" type="range" min={0} max={50} step={1} value={diffThreshold} onChange={(e) => setDiffThreshold(Number(e.target.value))} className="w-full" />
            </div>
          )}
        </ControlCard>

        <ControlCard tour="res-roi" title="Regiones de interés">
          <input aria-label="Etiqueta para el próximo ROI" placeholder="Etiqueta (opcional)" className="input py-1.5" value={roiLabel} onChange={(e) => setRoiLabel(e.target.value)} maxLength={80} />
          <button type="button" aria-pressed={drawMode} className={`w-full ${drawMode ? "btn-primary" : "btn-secondary"}`} onClick={() => setDrawMode((d) => !d)}>
            <Icon name="square" className="h-4 w-4" />
            {drawMode ? "Dibujando… (clic para salir)" : "Dibujar ROI"}
          </button>
          {drawMode && <p className="text-xs text-slate-500">Arrastra sobre cualquiera de las imágenes.</p>}
        </ControlCard>

        <ControlCard tour="res-export" title="Exportar PNG">
          {exportsOff && <p className="text-xs text-amber-800">{DEMO_EXPORT_NOTE}.</p>}
          <div className="grid grid-cols-2 gap-1.5">
            <button type="button" className="btn-secondary px-2 py-1.5 text-xs" disabled={exportsOff || !a || !img} onClick={() => exportPng("A")}>
              Grupo A
            </button>
            <button type="button" className="btn-secondary px-2 py-1.5 text-xs" disabled={exportsOff || !a || !img} onClick={() => exportPng("B")}>
              Grupo B
            </button>
            <button type="button" className="btn-secondary px-2 py-1.5 text-xs" disabled={exportsOff || !a?.difference || !img} onClick={() => exportPng("diff")}>
              Diferencia
            </button>
            <button type="button" className="btn-secondary px-2 py-1.5 text-xs" disabled={exportsOff || !a || !img} onClick={() => exportPng("comparison")} data-testid="export-comparison-png">
              Comparativa A | B
            </button>
          </div>
        </ControlCard>
      </aside>

      {/* ---------------- Área principal ---------------- */}
      <div className="min-w-0 space-y-3">
        <div className="space-y-2">
          {source === "synthetic" && (
            <div className="flex items-center gap-3 rounded-xl bg-fuchsia-50 px-3 py-2 ring-2 ring-fuchsia-500" data-testid="synthetic-results-badge" role="note">
              <span className="rounded-md bg-fuchsia-700 px-2.5 py-0.5 text-sm font-black tracking-widest text-white">SINTÉTICO</span>
              <span className="text-sm text-fuchsia-950">Datos de demostración generados por computadora. No provienen de personas y nunca se combinan con datos reales.</span>
            </div>
          )}
          {source === "webgazer" && study.sessionCounts.synthetic.total > 0 && study.sessionCounts.webgazer.completed === 0 && (
            <NoticeStrip tone="info" testId="synthetic-banner">
              Aún no hay sesiones reales completadas. Para ver la demostración elige la fuente <strong>«SINTÉTICA (demo)»</strong> en el panel de la izquierda.
            </NoticeStrip>
          )}
        </div>
        <ErrorAlert error={error ?? analysis.error ?? shared.error ?? rois.error} />

        {image && (
          <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm" aria-label="Mapas de calor" data-tour="res-maps">
            <div className="mb-3 grid items-start gap-3 lg:grid-cols-[minmax(0,1fr)_auto]">
              <div className="min-w-0">
                <h2 className="truncate text-base font-bold text-ink">
                  {mode === "side" ? "Distribución de atención estimada" : "Diferencia firmada A − B"}
                  {analysis.loading && <span className="ml-2 text-sm font-normal text-sky-700">Calculando…</span>}
                </h2>
                {/* Aviso metodológico siempre visible junto a los mapas. */}
                <p className="mt-0.5 flex items-start gap-1.5 text-xs text-amber-800">
                  <Icon name="alert" className="mt-px h-3.5 w-3.5 shrink-0" />
                  <span>{EXPLORATORY_WARNING}</span>
                </p>
                <p className="mt-0.5 text-xs text-slate-500">Rueda del ratón para acercar · arrastra para desplazar · los paneles se mueven juntos.</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-60">
                  {mode === "side" ? (
                    <SequentialLegend max={a?.sharedMax ?? 0} />
                  ) : (
                    <DivergingLegend maxAbs={a?.difference?.max ?? 0} threshold={diffThreshold / 100} groupA={`A`} groupB={`B`} />
                  )}
                </div>
                {view.scale > 1 && (
                  <button type="button" className="btn-secondary px-2.5 py-1.5 text-xs" onClick={() => setView(IDENTITY_VIEW)}>
                    <Icon name="zoomReset" className="h-4 w-4" /> Restablecer zoom
                  </button>
                )}
              </div>
            </div>

            {mode === "side" ? (
              <div className="grid gap-4 lg:grid-cols-2">
                <HeatmapPanel
                  {...panelCommon}
                  testId="panel-a"
                  maxHeight={panelMaxH}
                  title={`A: ${groupName(groupA)} · n = ${a?.groupA.n ?? "…"}`}
                  overlay={overlayA}
                  emptyMessage={a && a.groupA.n === 0 ? "Sin participantes elegibles" : undefined}
                />
                <HeatmapPanel
                  {...panelCommon}
                  testId="panel-b"
                  maxHeight={panelMaxH}
                  title={`B: ${groupName(groupB)} · n = ${a?.groupB.n ?? "…"}`}
                  overlay={overlayB}
                  emptyMessage={a && a.groupB.n === 0 ? "Sin participantes elegibles" : undefined}
                />
              </div>
            ) : (
              <div className="mx-auto max-w-4xl">
                <HeatmapPanel
                  {...panelCommon}
                  testId="panel-diff"
                  maxHeight={panelMaxH}
                  title={`Diferencia A − B · nA = ${a?.groupA.n ?? "…"}, nB = ${a?.groupB.n ?? "…"}`}
                  overlay={overlayDiff}
                  emptyMessage={a && !a.difference ? "Se necesita al menos un participante elegible en cada grupo" : undefined}
                />
              </div>
            )}
          </section>
        )}

        {image && (
          <section className="rounded-2xl border border-slate-200 bg-white shadow-sm" aria-label="Detalle del análisis" data-tour="res-tabs">
            <div className="px-3 pt-1">
              <Tabs
                label="Detalle del análisis"
                value={tab}
                onChange={setTab}
                tabs={[
                  { value: "metrics", label: "Métricas A vs B" },
                  { value: "rois", label: "Regiones de interés", badge: rois.data?.length ?? 0 },
                  { value: "exclusions", label: "Exclusiones" },
                  { value: "warnings", label: "Advertencias", badge: a?.warnings.length ?? 0 },
                  { value: "method", label: "Método" },
                ]}
              />
            </div>
            <div className="p-3">
              {tab === "metrics" && (a ? <GroupCompare a={a.groupA} b={a.groupB} /> : <Spinner />)}
              {tab === "rois" &&
                (rois.loading && !rois.data ? (
                  <Spinner />
                ) : (
                  <RoiTable
                    rois={rois.data ?? []}
                    analysis={a}
                    onHover={setHoverRoi}
                    onRename={(r, label) => roiAction(() => api.updateRoi(study.id, r.id, { label }))}
                    onDelete={(r) => roiAction(() => api.deleteRoi(study.id, r.id))}
                  />
                ))}
              {tab === "exclusions" && (a ? <ExclusionCompare a={a.groupA} b={a.groupB} /> : <Spinner />)}
              {tab === "warnings" &&
                (a && a.warnings.length > 0 ? (
                  <ul className="space-y-1.5 px-1 text-sm text-amber-950">
                    {a.warnings.map((w) => (
                      <li key={w} className="flex items-start gap-2">
                        <Icon name="alert" className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
                        {w}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="px-1 text-sm text-slate-500">Sin advertencias para esta comparación.</p>
                ))}
              {tab === "method" && (
                <div className="max-w-3xl space-y-2 px-1 text-sm text-slate-600">
                  <p>
                    Cada participante elegible aporta un mapa normalizado (suma 1) de sus muestras estimadas de mirada sobre la imagen; el mapa del grupo es el promedio, por lo que
                    cada persona pesa igual. Los mapas muestran distribución espacial relativa, no tiempo absoluto.
                  </p>
                  <p>
                    A y B usan la misma escala de color. En la diferencia, azul = relativamente más en A y naranja = relativamente más en B; por debajo del umbral no se colorea.
                  </p>
                  <p className="font-util text-xs text-slate-500">
                    Algoritmo {a?.algorithmVersion ?? ALGORITHM_VERSION}
                    {a?.cached ? " · en caché" : ""} · σ = {(sigma * 100).toFixed(1)} % del lado largo
                  </p>
                </div>
              )}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
